import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  TEXT_OPTION_TYPES,
  TEXT_OPTION_WRITE_ROLES,
  useProductTextOptionMutations,
  useProductTextOptionsQuery,
  type TextOption,
  type TextOptionChoice,
  type TextOptionRequest,
  type TextOptionType,
} from './useProductTextOptions';

/*
 * 상품 수정 > 텍스트 옵션 — 구매 시 고객이 옵션 줄마다 입력하는 값(고도몰 텍스트옵션, 예: "국적 Nationality").
 * 주문당 한 번 받는 "추가 입력 · 동의"(productCollection)와 다른 기능이라 영역을 나눈다.
 * 상품 저장 버튼과 별개로 행마다 즉시 저장한다(catalog /options API). AUDITOR 는 조회만.
 */

interface Draft {
  name: string;
  optionType: TextOptionType;
  required: boolean;
  maxLength: string;
  position: string;
  choices: Array<{ choiceId?: string | null; label: string; additionalAmount: string }>;
}

const isTextType = (type: TextOptionType) => type === 'TEXT' || type === 'TEXTAREA';

const toDraft = (o: TextOption): Draft => ({
  name: o.name,
  optionType: o.optionType,
  required: o.required,
  maxLength: o.maxLength != null ? String(o.maxLength) : '',
  position: String(o.position),
  choices: o.choices.map((c) => ({
    choiceId: c.choiceId ?? null,
    label: c.label,
    additionalAmount: c.additionalAmount != null ? String(c.additionalAmount) : '',
  })),
});

const emptyDraft = (position: number): Draft => ({
  name: '',
  optionType: 'TEXT',
  required: false,
  maxLength: '',
  position: String(position),
  choices: [],
});

const isWholeNumber = (v: string) => /^\d+$/.test(v.trim());

export const ProductTextOptionsSection = ({ productId }: { productId: string }) => {
  const { t } = useTranslation('products');
  const query = useProductTextOptionsQuery(productId);
  const { create, update, remove } = useProductTextOptionMutations(productId);
  const canWrite = useHasRole(TEXT_OPTION_WRITE_ROLES);

  const [editing, setEditing] = useState<{ optionId: string | null; draft: Draft } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const options = query.data ?? [];
  const typeLabel = (type: TextOptionType) =>
    ({
      TEXT: t('products.textOptions.type.TEXT', '텍스트'),
      TEXTAREA: t('products.textOptions.type.TEXTAREA', '여러 줄 텍스트'),
      NUMBER: t('products.textOptions.type.NUMBER', '숫자'),
      SELECT: t('products.textOptions.type.SELECT', '선택형'),
    })[type];

  const openCreate = () => {
    const nextPosition = options.length ? Math.max(...options.map((o) => o.position)) + 1 : 1;
    setFormError(null);
    setEditing({ optionId: null, draft: emptyDraft(nextPosition) });
  };
  const openEdit = (o: TextOption) => {
    setFormError(null);
    setEditing({ optionId: o.optionId, draft: toDraft(o) });
  };
  const patch = (p: Partial<Draft>) =>
    setEditing((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...p } } : prev));

  const validate = (d: Draft): string | TextOptionRequest => {
    const name = d.name.trim();
    if (!name) return t('products.textOptions.error.name', '옵션 이름을 입력해 주세요.');
    if (name.length > 100) return t('products.textOptions.error.nameLength', '옵션 이름은 100자 이내로 입력해 주세요.');
    if (isTextType(d.optionType) && d.maxLength.trim() !== '' && (!isWholeNumber(d.maxLength) || Number(d.maxLength) < 1)) {
      return t('products.textOptions.error.maxLength', '최대 글자 수는 1 이상의 숫자로 입력해 주세요.');
    }
    if (!isWholeNumber(d.position)) return t('products.textOptions.error.position', '순서는 0 이상의 숫자로 입력해 주세요.');
    let choices: TextOptionChoice[] = [];
    if (d.optionType === 'SELECT') {
      if (d.choices.length === 0 || d.choices.some((c) => !c.label.trim())) {
        return t('products.textOptions.error.choices', '선택지 이름을 하나 이상 입력해 주세요.');
      }
      if (d.choices.some((c) => c.additionalAmount.trim() !== '' && Number.isNaN(Number(c.additionalAmount)))) {
        return t('products.textOptions.error.amount', '추가금액은 숫자로 입력해 주세요.');
      }
      choices = d.choices.map((c, i) => ({
        choiceId: c.choiceId ?? null,
        label: c.label.trim(),
        additionalAmount: c.additionalAmount.trim() === '' ? null : Number(c.additionalAmount),
        position: i,
      }));
    }
    return {
      name,
      optionType: d.optionType,
      required: d.required,
      maxLength: isTextType(d.optionType) && d.maxLength.trim() !== '' ? Number(d.maxLength) : null,
      position: Number(d.position),
      choices,
    };
  };

  const save = async () => {
    if (!editing) return;
    const body = validate(editing.draft);
    if (typeof body === 'string') {
      setFormError(body);
      return;
    }
    try {
      if (editing.optionId) await update.mutateAsync({ optionId: editing.optionId, body });
      else await create.mutateAsync(body);
      setEditing(null);
    } catch (e) {
      setFormError(friendlyMessage(e, t('products.textOptions.error.save', '저장하지 못했습니다.')));
    }
  };

  const onDelete = async (o: TextOption) => {
    if (!window.confirm(t('products.textOptions.confirmDelete', { defaultValue: '"{{name}}" 옵션을 삭제할까요?', name: o.name }))) return;
    setRowError(null);
    try {
      await remove.mutateAsync(o.optionId);
    } catch (e) {
      setRowError(friendlyMessage(e, t('products.textOptions.error.delete', '삭제하지 못했습니다.')));
    }
  };

  const busy = create.isPending || update.isPending;
  const draft = editing?.draft;

  return (
    <section
      id="section-text-options"
      className="scroll-mt-24 rounded-lg p-6"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      data-testid="product-text-options"
    >
      <header
        className="mb-4 flex items-start justify-between gap-3 pb-4"
        style={{ borderBottom: '1px solid var(--divider-soft)' }}
      >
        <div>
          <h3
            className="text-[18px] font-bold"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--content-strong)', letterSpacing: '-0.01em' }}
          >
            {t('products.textOptions.title', '텍스트 옵션')}
          </h3>
          <p className="mt-1 text-[13px]" style={{ color: 'var(--content-muted)' }}>
            {t('products.textOptions.description', '구매 시 고객이 옵션 줄마다 입력합니다 (예: 국적). 주문당 한 번 받는 정보는 위 "추가 입력 · 동의"에서 설정합니다.')}
          </p>
        </div>
        {canWrite ? (
          <Button type="button" size="sm" variant="outline" onClick={openCreate} data-testid="text-option-add">
            {t('products.textOptions.add', '옵션 추가')}
          </Button>
        ) : null}
      </header>

      {rowError ? (
        <p role="alert" className="mb-3 text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {rowError}
        </p>
      ) : null}

      {query.isPending ? (
        <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {t('products.textOptions.loading', '불러오는 중…')}
        </p>
      ) : query.isError ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {friendlyMessage(query.error, t('products.textOptions.error.load', '텍스트 옵션을 불러오지 못했습니다.'))}
        </p>
      ) : options.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--content-muted)' }} data-testid="text-options-empty">
          {t('products.textOptions.empty', '등록된 텍스트 옵션이 없습니다.')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">{t('products.textOptions.col.position', '순서')}</TableHead>
              <TableHead>{t('products.textOptions.col.name', '이름')}</TableHead>
              <TableHead>{t('products.textOptions.col.type', '유형')}</TableHead>
              <TableHead>{t('products.textOptions.col.required', '필수')}</TableHead>
              <TableHead>{t('products.textOptions.col.maxLength', '최대 글자 수')}</TableHead>
              {canWrite ? <TableHead className="w-32" /> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {options.map((o) => (
              <TableRow key={o.optionId} data-testid="text-option-row">
                <TableCell className="tabular-nums">{o.position}</TableCell>
                <TableCell>
                  <div style={{ color: 'var(--content-strong)' }}>{o.name}</div>
                  {o.optionType === 'SELECT' && o.choices.length > 0 ? (
                    <div className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                      {o.choices
                        .map((c) =>
                          c.additionalAmount ? `${c.label} (+${c.additionalAmount.toLocaleString('ko-KR')})` : c.label,
                        )
                        .join(' · ')}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>{typeLabel(o.optionType)}</TableCell>
                <TableCell>
                  {o.required ? t('products.textOptions.required', '필수') : t('products.textOptions.optional', '선택')}
                </TableCell>
                <TableCell className="tabular-nums">
                  {isTextType(o.optionType) && o.maxLength
                    ? t('products.textOptions.maxLengthValue', { defaultValue: '{{count}}자', count: o.maxLength })
                    : '-'}
                </TableCell>
                {canWrite ? (
                  <TableCell className="whitespace-nowrap text-right">
                    <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(o)}>
                      {t('products.textOptions.edit', '수정')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => void onDelete(o)}
                      disabled={remove.isPending}
                    >
                      {t('products.textOptions.delete', '삭제')}
                    </Button>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? undefined : setEditing(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing?.optionId
                ? t('products.textOptions.editTitle', '텍스트 옵션 수정')
                : t('products.textOptions.addTitle', '텍스트 옵션 추가')}
            </DialogTitle>
            <DialogDescription>
              {t('products.textOptions.dialogHint', '구매 시 고객이 옵션 줄마다 입력합니다 (예: 국적).')}
            </DialogDescription>
          </DialogHeader>
          {draft ? (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <label className="flex flex-col gap-1 text-sm">
                {t('products.textOptions.col.name', '이름')}
                <Input
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  maxLength={100}
                  placeholder="국적 Nationality"
                  data-testid="text-option-name"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                {t('products.textOptions.col.type', '유형')}
                <Select
                  value={draft.optionType}
                  onChange={(e) => {
                    const optionType = e.target.value as TextOptionType;
                    patch({
                      optionType,
                      choices:
                        optionType === 'SELECT' && draft.choices.length === 0
                          ? [{ label: '', additionalAmount: '' }]
                          : draft.choices,
                    });
                  }}
                  data-testid="text-option-type"
                >
                  {TEXT_OPTION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {typeLabel(type)}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.required}
                  onCheckedChange={(v) => patch({ required: Boolean(v) })}
                  aria-label={t('products.textOptions.col.required', '필수')}
                  data-testid="text-option-required"
                />
                {t('products.textOptions.requiredLabel', '필수 입력')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                {isTextType(draft.optionType) ? (
                  <label className="flex flex-col gap-1 text-sm">
                    {t('products.textOptions.col.maxLength', '최대 글자 수')}
                    <Input
                      inputMode="numeric"
                      value={draft.maxLength}
                      onChange={(e) => patch({ maxLength: e.target.value })}
                      placeholder={t('products.textOptions.noLimit', '제한 없음')}
                      data-testid="text-option-max-length"
                    />
                  </label>
                ) : null}
                <label className="flex flex-col gap-1 text-sm">
                  {t('products.textOptions.col.position', '순서')}
                  <Input
                    inputMode="numeric"
                    value={draft.position}
                    onChange={(e) => patch({ position: e.target.value })}
                    data-testid="text-option-position"
                  />
                </label>
              </div>
              {draft.optionType === 'SELECT' ? (
                <fieldset className="flex flex-col gap-2" data-testid="text-option-choices">
                  <legend className="mb-1 text-sm">{t('products.textOptions.choices', '선택지')}</legend>
                  {draft.choices.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={c.label}
                        onChange={(e) =>
                          patch({ choices: draft.choices.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })
                        }
                        placeholder={t('products.textOptions.choiceLabel', '선택지 이름')}
                        maxLength={100}
                        aria-label={t('products.textOptions.choiceLabel', '선택지 이름')}
                      />
                      <Input
                        inputMode="numeric"
                        value={c.additionalAmount}
                        onChange={(e) =>
                          patch({
                            choices: draft.choices.map((x, j) => (j === i ? { ...x, additionalAmount: e.target.value } : x)),
                          })
                        }
                        placeholder={t('products.textOptions.additionalAmount', '추가금액')}
                        aria-label={t('products.textOptions.additionalAmount', '추가금액')}
                        className="max-w-[120px]"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => patch({ choices: draft.choices.filter((_, j) => j !== i) })}
                      >
                        {t('products.textOptions.delete', '삭제')}
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => patch({ choices: [...draft.choices, { label: '', additionalAmount: '' }] })}
                  >
                    {t('products.textOptions.addChoice', '선택지 추가')}
                  </Button>
                </fieldset>
              ) : null}
              {formError ? (
                <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
                  {formError}
                </p>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setEditing(null)} disabled={busy}>
                  {t('products.actions.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={busy} data-testid="text-option-save">
                  {t('products.actions.save')}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
};
